import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { LogIn, ShieldCheck, Ticket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/useAuth'
import { TicketCard } from '../components/TicketCard'
import { useMyTickets } from '../hooks/useMyTickets'

export function MyTicketsPage() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const ticketId = searchParams.get('ticketId')?.trim() || undefined
  const ownerAccessToken =
    searchParams.get('accessToken')?.trim() || searchParams.get('ownerAccessToken')?.trim() || undefined
  const { user } = useAuth()
  const { tickets, orders, loading, error, refresh } = useMyTickets({ ownerAccessToken, ticketId })
  const hasCredential = Boolean(user || ownerAccessToken)
  const pdfByOrderId = new Map(orders.map((order) => [order.id, order.pdfUrl]))
  const validTicketCount = tickets.filter((ticket) => ticket.status === 'valid' && !ticket.checkedIn).length

  if (loading) {
    return (
      <div className="container mx-auto max-w-5xl space-y-6 px-4 py-12">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-16 w-full rounded-xl" />
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton key={index} className="h-72 w-full rounded-2xl" />
        ))}
      </div>
    )
  }

  if (!hasCredential) {
    return (
      <div className="container mx-auto max-w-lg px-4 py-16">
        <div className="space-y-5 rounded-2xl border bg-card p-6 text-center shadow-sm sm:p-8">
          <ShieldCheck className="mx-auto h-12 w-12 text-primary" />
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Secure ticket access</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Open the secure ticket-management link from your email, or sign in to view tickets
              currently owned by your account.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button asChild>
              <Link
                to="/sign-in"
                state={{ returnTo: `${location.pathname}${location.search}` }}
              >
                <LogIn className="h-4 w-4" />
                Sign in
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/tickets/lookup">Request a secure link</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-5xl space-y-8 px-4 py-12">
      <div className="flex flex-col gap-4 border-b pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-medium text-primary">Secure ticket wallet</p>
          <h1 className="mt-1 text-3xl font-bold">{user ? 'My Tickets' : 'Your Tickets'}</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            These tickets follow their current owner, even if someone else originally purchased
            them. Transfer valid tickets until they are checked in.
          </p>
        </div>
        {tickets.length > 0 && (
          <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm">
            <span className="font-semibold">{tickets.length}</span> ticket{tickets.length === 1 ? '' : 's'}
            <span className="text-muted-foreground"> · </span>
            <span className="font-semibold">{validTicketCount}</span> available
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {tickets.length === 0 ? (
        <div className="space-y-4 py-16 text-center">
          <Ticket className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">No current tickets found</h2>
            <p className="text-sm text-muted-foreground">
              Tickets will appear here after payment or after a recipient claims a transfer.
            </p>
          </div>
          <Button asChild>
            <Link to="/events">Browse Events</Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-6">
          {tickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              pdfUrl={ticket.orderId ? pdfByOrderId.get(ticket.orderId) : undefined}
              ownerAccessToken={ownerAccessToken}
              onRefresh={refresh}
            />
          ))}
        </div>
      )}
    </div>
  )
}
